import { CheckIcon } from "@heroicons/react/20/solid";
import { cn } from "@/lib/utils";

export type Step = {
  id: string;
  name: string;
  description?: string;
};

export function Steps({
  steps,
  currentIndex,
  onSelect,
}: {
  steps: Step[];
  currentIndex: number;
  onSelect?: (index: number) => void;
}) {
  return (
    <nav aria-label="Progress">
      <ol role="list" className="flex items-center gap-2">
        {steps.map((step, index) => {
          const isComplete = index < currentIndex;
          const isCurrent = index === currentIndex;
          const canSelect = !!onSelect && index < currentIndex;

          return (
            <li key={step.id} className="flex flex-1 items-center gap-2">
              <button
                type="button"
                disabled={!canSelect}
                onClick={() => onSelect?.(index)}
                className={cn(
                  "flex min-w-0 items-center gap-2 text-left",
                  canSelect && "cursor-pointer",
                )}
              >
                <span
                  className={cn(
                    "flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                    isComplete && "bg-blue-600 text-white",
                    isCurrent && "border-2 border-blue-600 text-blue-600",
                    !isComplete &&
                      !isCurrent &&
                      "border-2 border-gray-300 text-gray-500",
                  )}
                >
                  {isComplete ? <CheckIcon className="size-4" /> : index + 1}
                </span>
                <span
                  className={cn(
                    "truncate text-sm font-medium",
                    isCurrent ? "text-blue-600" : "text-gray-500",
                    isComplete && "text-gray-900",
                  )}
                >
                  {step.name}
                </span>
              </button>
              {index < steps.length - 1 ? (
                <span
                  aria-hidden="true"
                  className={cn(
                    "h-0.5 flex-1 rounded-full",
                    isComplete ? "bg-blue-600" : "bg-gray-200",
                  )}
                />
              ) : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
